"use client";

import { useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type ClaimErrorBody = { error?: { message?: string } };

/**
 * "Is this your firm?" action on a public profile — CONTRACTS §5 (D owns
 * the route: `POST /api/firms/[id]/claim`). Handles 401 (redirect to
 * sign-in with a way back), 201 (success toast), 409 (informative toast).
 */
export function ClaimButton({
  firmId,
  className,
}: {
  firmId: string;
  className?: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [pending, setPending] = useState(false);

  async function handleClaim() {
    if (pending) return;
    setPending(true);
    try {
      const res = await fetch(`/api/firms/${firmId}/claim`, {
        method: "POST",
      });

      if (res.status === 401) {
        const qs = searchParams.toString();
        const redirectTo = qs ? `${pathname}?${qs}` : pathname;
        router.push(`/sign-in?redirect_url=${encodeURIComponent(redirectTo)}`);
        return;
      }

      if (res.status === 201) {
        toast.success("Claim submitted — an administrator will review it");
        return;
      }

      if (res.status === 409) {
        const body = (await res.json().catch(() => null)) as ClaimErrorBody | null;
        toast.info(
          body?.error?.message ??
            "You already have a pending claim, or this firm is already claimed."
        );
        return;
      }

      const body = (await res.json().catch(() => null)) as ClaimErrorBody | null;
      toast.error(body?.error?.message ?? "Could not submit claim. Try again.");
    } catch {
      toast.error(
        "Could not submit claim — check your connection and try again."
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <Button onClick={handleClaim} disabled={pending} className={cn(className)}>
      {pending ? "Submitting…" : "Claim this firm"}
    </Button>
  );
}
